import {
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import React from "react";
import { useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { useNavigate } from "react-router-dom";
import { FakePlaidDialog } from "./FakePlaidDialog";

export const removeCustomerOnboardingId = () => {
  try {
    localStorage.removeItem("customerOnboardingId");
  } catch {}
  window.dispatchEvent(new Event("customerOnboardingId-changed"));
};

/**
 * Child component that mounts only when a linkToken exists.
 * Each mount creates a fresh usePlaidLink instance, which avoids
 * the stale-hook problem when re-opening Plaid Link a second time.
 */
const PlaidLinkOpener = ({ linkToken, onSuccess, onDone }) => {
  const [opened, setOpened] = useState(false);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (public_token, metadata) => {
      onSuccess(public_token, metadata);
    },
    onExit: () => {
      onDone();
    },
  });

  useEffect(() => {
    if (ready && !opened) {
      setOpened(true);
      open();
    }
  }, [ready, opened, open]);

  return null;
};

const LinkPlaidButton = ({
  customer_id,
  plaiditem_id,
  bankLinked,
  checkingPlaid = false,
  setStepperStep,
  onBankLinkedChange,
  onExchangeStart,
  product = "transactions",
  fontFam = "Rethink Sans",
  completeSetup = false,
  labelWhenUnlinked = "Link Bank Accounts",
  labelWhenLinked = "Bank Account Linked (Continue)",
  disableAutoAdvance = false,
  startIcon = null,
  buttonSx = {},
  autoOpenKey = null,
}) => {
  const [loadingToken, setLoadingToken] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showFake, setShowFake] = useState(false);
  const navigate = useNavigate();

  const formatError = (err) => {
    const data = err?.response?.data;

    if (typeof data === "string") return data;
    if (data?.detail) return data.detail;

    const plaid = data?.plaid;
    if (plaid?.error_message) return plaid.error_message;
    if (plaid?.display_message) return plaid.display_message;
    if (plaid?.error_code || plaid?.error_type) {
      return `${plaid.error_type || "PLAID_ERROR"}: ${plaid.error_code || ""}`.trim();
    }

    return err?.message || "Failed to open Plaid Link.";
  };

  const fetchLinkToken = async () => {
    if (checkingPlaid || loadingToken) return;
    setLoadingToken(true);
    try {
      const params = plaiditem_id ? { plaiditem_id, product } : { product };

      const { data } = await axios.get(`/api/v2/create_link_token/`, {
        params,
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      setLinkToken(data.link_token);
    } catch (err) {
      console.error("❌ Failed to create link token:", err);
      setSnackbarMsg(formatError(err));
      setSnackbarOpen(true);
    } finally {
      setLoadingToken(false);
    }
  };

  const closePlaid = () => {
    setLinkToken(null);
    setExchanging(false);
  };

  const handlePlaidSuccess = async (public_token, metadata) => {
    try {
      setExchanging(true);
      onExchangeStart?.();
      await axios.post("/api/v2/exchange_public_token/", {
        public_token,
        customer_id,
        refresh: Boolean(plaiditem_id),
        product,
      });
      onBankLinkedChange?.(true);
    } catch (err) {
      console.error("❌ Plaid exchange failed:", err);
      setSnackbarMsg(formatError(err));
      setSnackbarOpen(true);
    } finally {
      setExchanging(false);
      setLinkToken(null);
    }
  };

  useEffect(() => {
    if (!autoOpenKey) return;
    if (bankLinked) return;
    if (checkingPlaid || loadingToken || exchanging) return;

    fetchLinkToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenKey]);

  if (checkingPlaid) {
    return (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          disabled
          startIcon={<CircularProgress size={16} color="inherit" />}
          sx={{
            fontFamily: fontFam,
            textTransform: "none",
            borderRadius: "4px",
            backgroundColor: "#536DEC",
            "&:hover": { backgroundColor: "#4C63D7" },
          }}
        >
          Checking bank link...
        </Button>
      </Box>
    );
  }

  const buttonLabel = bankLinked ? labelWhenLinked : labelWhenUnlinked;

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={
          (loadingToken || exchanging) && !bankLinked ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            startIcon
          )
        }
        onClick={(event) => {
          if (event.shiftKey) {
            setShowFake(true);
            return;
          }

          if (bankLinked) {
            if (completeSetup) {
              sessionStorage.setItem("selectedCustomerId", customer_id);
              removeCustomerOnboardingId();
              navigate("/");
            } else {
              if (!disableAutoAdvance) {
                setStepperStep?.(2);
              }
            }
          } else {
            fetchLinkToken();
          }
        }}
        disabled={loadingToken || exchanging}
        sx={{
          fontFamily: fontFam,
          textTransform: "none",
          borderRadius: "4px",
          backgroundColor: "#536DEC",
          "&:hover": { backgroundColor: "#4C63D7" },
          ...buttonSx,
        }}
      >
        {buttonLabel}
      </Button>

      {linkToken && (
        <PlaidLinkOpener
          linkToken={linkToken}
          onSuccess={handlePlaidSuccess}
          onDone={closePlaid}
        />
      )}

      <FakePlaidDialog
        open={showFake}
        onClose={() => setShowFake(false)}
        customer_id={customer_id}
        onSuccess={() => {
          onBankLinkedChange?.(true);
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={8000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LinkPlaidButton;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
